import { useState, useEffect, useMemo } from 'react';
import { getOrganizationData } from '../services/organizationS3Service';
import { Organization, Portfolio } from '../services/organizationService';
import { resolveConfig, ResolvedConfig } from '../utils/configResolver';
import { setOrgId, setFieldMappings } from '../services/asanaService';

/**
 * Flattened portfolio option for the combined dropdown.
 * Each option represents one selectable portfolio with its parent org context.
 */
export interface PortfolioOption {
  /** Unique key: "orgId::portfolioId" */
  key: string;
  /** Display label: "OrgName → PortfolioName" or just "PortfolioName" for single-portfolio orgs */
  label: string;
  orgId: string;
  orgName: string;
  portfolioId: string;
  portfolioName: string;
  asanaProjectId: string;
  inheritConfig: boolean;
}

/**
 * Custom hook that manages organization/portfolio data and selection.
 * 
 * Provides a flattened list of portfolio options for a single combined dropdown
 * (Option B: "OrgName → PortfolioName") and resolves the config for the selected portfolio.
 */
export const useOrganizations = () => {
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Loads organizations from S3 and flattens into portfolio options
   */
  const loadOrganizations = async () => {
    console.log('🏢 ORGANIZATIONS: Loading available portfolios');
    setLoading(true);
    setError(null);
    
    try {
      const orgData = await getOrganizationData();
      if (orgData && orgData.organizations.length > 0) {
        console.log(`✅ ORGANIZATIONS: Loaded ${orgData.organizations.length} organizations`);
        setOrganizations(orgData.organizations);
        
        // Auto-select first portfolio
        const firstOrg = orgData.organizations[0];
        const portfolios = (firstOrg as any).portfolios as Portfolio[] | undefined;
        
        // Check if there's an orgId in the URL to auto-select
        const urlOrgId = window.location.pathname.match(/\/org\/([^/]+)/)?.[1];
        
        let autoSelected = false;
        if (urlOrgId) {
          // Find the org matching the URL
          const urlOrg = orgData.organizations.find((o: any) => o.id === urlOrgId);
          if (urlOrg) {
            const urlPortfolios = (urlOrg as any).portfolios as Portfolio[] | undefined;
            if (urlPortfolios && urlPortfolios.length > 0) {
              const key = `${urlOrg.id}::${urlPortfolios[0].id}`;
              setSelectedKey(key);
              console.log(`🎯 URL: Auto-selected ${urlOrg.name} → ${urlPortfolios[0].name}`);
              autoSelected = true;
            } else if (urlOrg.asanaProject) {
              const key = `${urlOrg.id}::legacy`;
              setSelectedKey(key);
              console.log(`🎯 URL (legacy): Auto-selected ${urlOrg.name}`);
              autoSelected = true;
            }
          }
        }
        
        if (!autoSelected) {
          if (portfolios && portfolios.length > 0) {
            // New model: select first portfolio
            const key = `${firstOrg.id}::${portfolios[0].id}`;
            setSelectedKey(key);
            console.log(`🎯 DEFAULT: Auto-selected ${firstOrg.name} → ${portfolios[0].name}`);
          } else if (firstOrg.asanaProject) {
            // Legacy model: org has asanaProject directly (no portfolios)
            const key = `${firstOrg.id}::legacy`;
            setSelectedKey(key);
            console.log(`🎯 DEFAULT (legacy): Auto-selected ${firstOrg.name}`);
          }
        }
      } else {
        console.log('⚠️ ORGANIZATIONS: No organizations found');
        setOrganizations([]);
        setError('No organizations available');
      }
    } catch (err) {
      console.error('❌ ORGANIZATIONS ERROR:', err);
      setOrganizations([]);
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Build flattened portfolio options for the dropdown
   */
  const portfolioOptions: PortfolioOption[] = useMemo(() => {
    const options: PortfolioOption[] = [];
    
    for (const org of organizations) {
      const portfolios = (org as any).portfolios as Portfolio[] | undefined;
      
      if (portfolios && portfolios.length > 0) {
        // New model: org has portfolios
        const showOrgPrefix = organizations.length > 1 || portfolios.length > 1;
        
        for (const p of portfolios) {
          options.push({
            key: `${org.id}::${p.id}`,
            label: showOrgPrefix ? `${org.name} → ${p.name}` : p.name,
            orgId: org.id,
            orgName: org.name,
            portfolioId: p.id,
            portfolioName: p.name,
            asanaProjectId: p.asanaProjectId,
            inheritConfig: p.inheritConfig,
          });
        }
      } else if (org.asanaProject) {
        // Legacy model: org has asanaProject directly
        options.push({
          key: `${org.id}::legacy`,
          label: org.name,
          orgId: org.id,
          orgName: org.name,
          portfolioId: 'legacy',
          portfolioName: org.name,
          asanaProjectId: org.asanaProject,
          inheritConfig: true,
        });
      }
    }
    
    return options;
  }, [organizations]);

  /**
   * Get the selected option, org, and portfolio objects
   */
  const selectedOption = portfolioOptions.find(o => o.key === selectedKey) || null;
  
  const selectedOrg = selectedOption
    ? organizations.find((o: any) => o.id === selectedOption.orgId) || null
    : null;
  
  const selectedPortfolioObj = selectedOption && selectedOrg && selectedOption.portfolioId !== 'legacy'
    ? ((selectedOrg as any).portfolios as Portfolio[] || []).find(p => p.id === selectedOption.portfolioId) || null
    : null;

  /**
   * Resolved config for the selected portfolio
   */
  const resolvedConfig: ResolvedConfig | null = useMemo(() => {
    if (!selectedOrg) return null;
    const config = resolveConfig(selectedPortfolioObj || undefined, selectedOrg as Organization);
    // Auto-set org ID and field mappings whenever resolved config changes
    // This ensures all pages (Tasks, Roadmap, Analytics) have the correct config
    setOrgId(selectedOrg.id);
    setFieldMappings(config.fieldMappings);
    return config;
  }, [selectedOrg, selectedPortfolioObj]);

  /**
   * Handle portfolio selection change
   */
  const handlePortfolioChange = (key: string) => {
    console.log(`🔄 PORTFOLIO CHANGE: ${key}`);
    setSelectedKey(key);
  };

  // Backward compatibility: expose selectedPortfolio as the asanaProjectId
  // This allows existing code that uses selectedPortfolio as a project ID to keep working
  const selectedPortfolio = selectedOption?.asanaProjectId || '';

  // Also expose availableOrganizations for backward compat
  const availableOrganizations = organizations;

  useEffect(() => {
    loadOrganizations();
  }, []);

  return {
    // New API
    portfolioOptions,
    selectedKey,
    selectedOption,
    selectedOrg,
    selectedPortfolioObj,
    resolvedConfig,
    setSelectedKey: handlePortfolioChange,
    
    // Backward compatible API
    availableOrganizations,
    selectedPortfolio,
    loading,
    error,
    hasValidSelection: !!selectedOption,
    selectedOrganization: selectedOrg,
    setSelectedPortfolio: handlePortfolioChange,
    reloadOrganizations: loadOrganizations,
    
    // Direct state setters
    setAvailableOrganizations: setOrganizations,
    setLoading,
    setError,
  };
};

export default useOrganizations;